import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, LogOut, Pencil, Plus, Store, Trash2, X } from "lucide-react";
import { API, api, formatApiErrorDetail, productImages } from "../api";
import { useAuth } from "../AuthContext";
import Logo from "../components/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const EMPTY_FORM = {
  name: "",
  tagline: "",
  description: "",
  specs: "",
  price: "",
  old_price: "",
  stock: "0",
  active: true,
  flavorsText: "",
};

const formFromProduct = (p) => ({
  name: p.name || "",
  tagline: p.tagline || "",
  description: p.description || "",
  specs: (p.specs || []).join(", "),
  price: String(p.price ?? ""),
  old_price: p.old_price != null ? String(p.old_price) : "",
  stock: String(p.stock ?? 0),
  active: p.active !== false,
  flavorsText: (p.flavors || []).join("\n"),
});

const formatPrice = (n) => `${Number(n).toFixed(2).replace(".", ",")}€`;

function ImageUploader({ product, onUploaded }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const images = productImages(product);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona un archivo de imagen (JPG, PNG, WebP)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(`/admin/products/${product.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(r.data);
      toast.success("Imagen añadida");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeImage = async (idx) => {
    try {
      const r = await api.delete(`/admin/products/${product.id}/images/${idx}`);
      onUploaded(r.data);
      toast.success("Imagen eliminada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <Label className="text-[#8A8F98]">Fotos del producto ({images.length})</Label>
      {images.length > 0 && (
        <div className="mt-1.5 grid grid-cols-4 gap-2">
          {images.map((path, i) => (
            <div
              key={path}
              className="relative aspect-square overflow-hidden rounded-lg border border-[#2D323E] bg-[#16181D]"
            >
              <img
                src={`${API}/images/${path}`}
                alt={`${product.name} ${i + 1}`}
                className="h-full w-full object-contain"
              />
              <button
                type="button"
                data-testid={`admin-image-delete-${product.id}-${i}`}
                onClick={() => removeImage(i)}
                aria-label={`Eliminar imagen ${i + 1}`}
                className="absolute right-1 top-1 rounded-full bg-[#16181D]/85 p-1 text-[#8A8F98] transition-colors hover:text-[#EF4444]"
              >
                <X className="h-3 w-3" />
              </button>
              {i === 0 && (
                <span className="absolute left-1 top-1 rounded bg-[#2EE6D6]/90 px-1.5 py-0.5 text-[9px] font-bold text-black">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <div
        data-testid={`admin-image-upload-${product.id}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          upload(e.dataTransfer.files?.[0]);
        }}
        className={`mt-2 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 text-center transition-colors ${
          drag ? "border-[#2EE6D6] bg-[#2EE6D6]/10" : "border-[#2D323E] hover:border-[#7C5CFF]/60"
        }`}
      >
        <ImagePlus className="h-7 w-7 text-[#8A8F98]" />
        <p className="text-xs text-[#8A8F98]">
          {uploading
            ? "Subiendo imagen…"
            : "Añade más imágenes: arrastra aquí o haz clic para seleccionarlas (JPG, PNG, WebP · máx. 8MB)"}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`admin-image-input-${product.id}`}
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pr, rr] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/referrals"),
      ]);
      setProducts(pr.data);
      setReferrals(rr.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  const removeReferral = async (code) => {
    if (!window.confirm(`¿Eliminar el código ${code}?`)) return;
    try {
      await api.delete(`/admin/referrals/${code}`);
      setReferrals((prev) => prev.filter((r) => r.code !== code));
      toast.success("Código eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm(formFromProduct(p));
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      tagline: form.tagline.trim(),
      description: form.description.trim(),
      specs: form.specs.split(",").map((s) => s.trim()).filter(Boolean),
      price: parseFloat(String(form.price).replace(",", ".")) || 0,
      old_price: form.old_price ? parseFloat(String(form.old_price).replace(",", ".")) : null,
      stock: parseInt(form.stock, 10) || 0,
      active: form.active,
      flavors: form.flavorsText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing?.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Producto actualizado");
        setDialogOpen(false);
      } else {
        const r = await api.post("/admin/products", payload);
        toast.success("Producto creado. Ahora puedes subir su foto.");
        setEditing(r.data);
        setForm(formFromProduct(r.data));
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      const r = await api.put(`/admin/products/${p.id}`, {
        name: p.name,
        tagline: p.tagline || "",
        description: p.description || "",
        specs: p.specs || [],
        price: p.price,
        old_price: p.old_price ?? null,
        stock: p.stock ?? 0,
        active: !p.active,
        flavors: p.flavors || [],
      });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? r.data : x)));
      toast.success(r.data.active ? "Producto activado" : "Producto desactivado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}" definitivamente?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Producto eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    navigate("/admin/login", { replace: true });
  };

  const activeCount = products.filter((p) => p.active).length;
  const outOfStock = products.filter((p) => (p.stock ?? 0) <= 0).length;

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#16181D] text-[#F2F4F6]">
      <header className="sticky top-0 z-40 border-b border-[#2D323E] bg-[#16181D]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-8">
          <Logo />
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-view-store-btn"
              className="flex items-center gap-2 rounded-full border border-[#2D323E] px-4 py-2 text-sm font-semibold text-[#8A8F98] transition-colors hover:border-[#2EE6D6]/50 hover:text-[#2EE6D6]"
            >
              <Store className="h-4 w-4" /> Ver tienda
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-full border border-[#2D323E] px-4 py-2 text-sm font-semibold text-[#8A8F98] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]"
            >
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="font-heading text-2xl font-bold sm:text-3xl">Productos</h1>
            <p data-testid="admin-stats" className="mt-1 text-sm text-[#8A8F98]">
              {products.length} productos · {activeCount} activos · {outOfStock} sin stock
            </p>
          </div>
          <button
            onClick={openNew}
            data-testid="admin-add-product-btn"
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#2EE6D6] to-[#7C5CFF] px-5 py-2.5 text-sm font-bold text-black transition-opacity hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> Añadir producto
          </button>
        </div>

        {loading ? (
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-56 animate-pulse rounded-2xl border border-[#2D323E] bg-[#1E2128]" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="mt-16 text-center text-[#8A8F98]">
            Aún no hay productos. Pulsa «Añadir producto» para crear el primero.
          </p>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {products.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-product-row-${p.id}`}
                className={`rounded-2xl border bg-[#1E2128] p-5 transition-colors ${
                  p.active ? "border-[#2D323E]" : "border-[#2D323E] opacity-60"
                }`}
              >
                <div className="flex gap-4">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-[#16181D]">
                    {p.image_path ? (
                      <img
                        src={`${API}/images/${p.image_path}`}
                        alt={p.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <ImagePlus className="h-6 w-6 text-[#2D323E]" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-heading text-lg font-semibold">{p.name}</h3>
                    <p className="text-sm text-[#8A8F98]">
                      <span className="font-semibold text-[#2EE6D6]">{formatPrice(p.price)}</span>
                      {p.old_price != null && (
                        <span className="ml-2 line-through">{formatPrice(p.old_price)}</span>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-[#8A8F98]">
                      Stock: {p.stock ?? 0} · {(p.flavors || []).length} sabores
                      {!p.active && <span className="ml-2 font-semibold text-[#F59E0B]">· Oculto en la tienda</span>}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-[#2D323E] pt-4">
                  <label className="flex items-center gap-2 text-xs text-[#8A8F98]">
                    <Switch
                      checked={p.active}
                      onCheckedChange={() => toggleActive(p)}
                      data-testid={`admin-toggle-active-${p.id}`}
                    />
                    {p.active ? "Activo" : "Inactivo"}
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`admin-edit-btn-${p.id}`}
                      className="flex items-center gap-1.5 rounded-lg border border-[#2D323E] px-3 py-1.5 text-xs font-semibold text-[#F2F4F6] transition-colors hover:border-[#2EE6D6]/50 hover:text-[#2EE6D6]"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Editar
                    </button>
                    <button
                      onClick={() => remove(p)}
                      data-testid={`admin-delete-btn-${p.id}`}
                      className="flex items-center gap-1.5 rounded-lg border border-[#2D323E] px-3 py-1.5 text-xs font-semibold text-[#8A8F98] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <section data-testid="admin-referrals-section" className="mt-12">
          <h2 className="font-heading text-xl font-bold">Códigos de referido</h2>
          <p className="mt-1 text-sm text-[#8A8F98]">
            -10% para quien compra con un código · +10% acumulado para el dueño del código por cada
            uso (máx. 100% / 100€ de descuento). Los usos se registran cuando el cliente finaliza el
            pedido por WhatsApp.
          </p>
          {referrals.length === 0 ? (
            <p className="mt-4 text-sm text-[#8A8F98]">
              Aún no hay códigos generados. Tus clientes pueden crear el suyo en la sección
              «Referidos» de la tienda.
            </p>
          ) : (
            <div className="mt-4 space-y-2">
              {referrals.map((r) => (
                <div
                  key={r.code}
                  data-testid={`admin-referral-row-${r.code}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#2D323E] bg-[#1E2128] px-4 py-3"
                >
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-mono text-base font-bold text-[#2EE6D6]">{r.code}</span>
                    <span className="text-sm font-semibold">{r.owner_name}</span>
                    <span className="text-xs text-[#8A8F98]">{r.owner_contact}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-[#8A8F98]">
                      <span className="font-bold text-[#F2F4F6]">{r.uses}</span> usos ·{" "}
                      <span className="font-bold text-[#7C5CFF]">
                        {Math.min(r.uses * (r.reward_pct ?? 10), 100)}%
                      </span>{" "}
                      acumulado (máx. 100€)
                    </span>
                    <button
                      type="button"
                      data-testid={`admin-referral-delete-${r.code}`}
                      onClick={() => removeReferral(r.code)}
                      aria-label={`Eliminar código ${r.code}`}
                      className="rounded-lg border border-[#2D323E] p-2 text-[#8A8F98] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="admin-product-dialog"
          className="max-h-[90vh] overflow-y-auto border-[#2D323E] bg-[#1E2128] text-[#F2F4F6] sm:max-w-xl"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing ? `Editar: ${editing.name}` : "Nuevo producto"}
            </DialogTitle>
          </DialogHeader>

          {editing?.id && (
            <ImageUploader
              product={editing}
              onUploaded={(updated) => {
                setEditing(updated);
                setProducts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
              }}
            />
          )}

          <form onSubmit={save} className="space-y-4" data-testid="admin-product-form">
            <div>
              <Label htmlFor="pf-name" className="text-[#8A8F98]">Nombre *</Label>
              <Input
                id="pf-name"
                data-testid="product-form-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 border-[#2D323E] bg-[#16181D]"
                placeholder="Ej: Razz Bar 60K"
              />
            </div>
            <div>
              <Label htmlFor="pf-tagline" className="text-[#8A8F98]">Resumen corto</Label>
              <Input
                id="pf-tagline"
                data-testid="product-form-tagline-input"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                className="mt-1.5 border-[#2D323E] bg-[#16181D]"
                placeholder="Ej: 60.000 caladas · Batería 650mAh"
              />
            </div>
            <div>
              <Label htmlFor="pf-description" className="text-[#8A8F98]">Descripción</Label>
              <Textarea
                id="pf-description"
                data-testid="product-form-description-input"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1.5 border-[#2D323E] bg-[#16181D]"
              />
            </div>
            <div>
              <Label htmlFor="pf-specs" className="text-[#8A8F98]">Características (separadas por comas)</Label>
              <Input
                id="pf-specs"
                data-testid="product-form-specs-input"
                value={form.specs}
                onChange={(e) => setForm({ ...form, specs: e.target.value })}
                className="mt-1.5 border-[#2D323E] bg-[#16181D]"
                placeholder="Ej: 60.000 caladas, Batería 650mAh, 16 sabores"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="pf-price" className="text-[#8A8F98]">Precio (€) *</Label>
                <Input
                  id="pf-price"
                  data-testid="product-form-price-input"
                  required
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="mt-1.5 border-[#2D323E] bg-[#16181D]"
                  placeholder="6,49"
                />
              </div>
              <div>
                <Label htmlFor="pf-old-price" className="text-[#8A8F98]">Precio anterior (€)</Label>
                <Input
                  id="pf-old-price"
                  data-testid="product-form-old-price-input"
                  inputMode="decimal"
                  value={form.old_price}
                  onChange={(e) => setForm({ ...form, old_price: e.target.value })}
                  className="mt-1.5 border-[#2D323E] bg-[#16181D]"
                  placeholder="12,99"
                />
              </div>
              <div>
                <Label htmlFor="pf-stock" className="text-[#8A8F98]">Stock</Label>
                <Input
                  id="pf-stock"
                  data-testid="product-form-stock-input"
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  className="mt-1.5 border-[#2D323E] bg-[#16181D]"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pf-flavors" className="text-[#8A8F98]">Sabores / combos (uno por línea)</Label>
              <Textarea
                id="pf-flavors"
                data-testid="product-form-flavors-input"
                rows={6}
                value={form.flavorsText}
                onChange={(e) => setForm({ ...form, flavorsText: e.target.value })}
                className="mt-1.5 border-[#2D323E] bg-[#16181D]"
                placeholder={"Fresa y Plátano\nMelocotón Helado\nCoco Loco"}
              />
            </div>
            <label className="flex items-center gap-3 text-sm text-[#8A8F98]">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="product-form-active-switch"
              />
              Producto visible en la tienda
            </label>
            <button
              type="submit"
              data-testid="product-form-submit-btn"
              disabled={saving}
              className="w-full rounded-xl bg-gradient-to-r from-[#2EE6D6] to-[#7C5CFF] py-3 font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear producto"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
